import { convexQuery } from "@convex-dev/react-query";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { ShieldAlert } from "lucide-react";

import { posDevicesGetDevicesByLocationId } from "~/api-refs";
import { DevicesList } from "~/components/devices/devices-list";
import { LoadingPanel } from "~/components/shared/loading-panel";
import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Skeleton } from "~/components/ui/skeleton";
import { useEmployeePermissions } from "~/hooks/use-employee-permissions";
import { toConvexTableId } from "~/lib/convex-table-id";

export const Route = createFileRoute("/_authenticated/locations/$id/devices")({
  component: DevicesPage,
});

function DevicesPage() {
  const { id } = Route.useParams();
  const {
    hasPermission,
    isLoading: permissionsLoading,
    hasEmployee,
  } = useEmployeePermissions();
  const canViewIntegrations = hasPermission("viewIntegrations");

  const { data: devices } = useQuery({
    ...convexQuery(posDevicesGetDevicesByLocationId, {
      locationId: toConvexTableId("locations", id),
    }),
    enabled: canViewIntegrations,
  });

  // Loading state
  if (permissionsLoading) {
    return <Skeleton className="h-96 w-full" />;
  }

  // No employee record linked
  if (!hasEmployee) {
    return (
      <Alert>
        <ShieldAlert className="h-4 w-4" />
        <AlertTitle>No Employee Profile</AlertTitle>
        <AlertDescription>
          Your account is not linked to an employee profile. Contact your
          organization admin to set up your employee record with the appropriate
          permissions.
        </AlertDescription>
      </Alert>
    );
  }

  // No permission to view devices
  if (!canViewIntegrations) {
    return (
      <Alert variant="destructive">
        <ShieldAlert className="h-4 w-4" />
        <AlertTitle>Access Denied</AlertTitle>
        <AlertDescription>
          You don't have permission to view devices and integrations. Contact
          your manager to request access.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="flex flex-col justify-between gap-6 py-5">
      <div className="space-y-1">
        <h2 className="body text-foreground text-weight-medium text-balance">
          Start / End of Day
        </h2>
        <p className="body-sm text-muted-foreground leading-tight">
          Configure start and end of day for POS devices at this location
        </p>
      </div>
      {devices ? (
        <DevicesList data={devices} />
      ) : (
        <LoadingPanel message="Loading POS devices..." />
      )}
    </div>
  );
}
